import { CreateCardCarouselItem, isHexColorLight, State, usePlayerState, useUserSettings, useViewport } from '@bpm-web-app/utils';
import classNames from 'classnames';
import { memo, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { Genre, SoundPackage } from '@bpm-web-app/create-api-sdk';
import { useDrag } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import FavoriteHeart from '../../../assets/icons/favorite-heart-outline.svg';
import UnfavoriteHeart from '../../../assets/icons/favorite-heart.svg';
import ThreeDotsIcon from '../../../assets/icons/more-dots-vert.svg';
import PlayArrow from '../../../assets/icons/player-play-alt.svg';
import PauseIcon from '../../../assets/icons/player-pause-lg.svg';
import styles from './card.module.css';
import { MissingImgPlaceholder } from '../ui';
import { CreateActionType, CreateThreeDotsSheetContext } from '../three-dots-sheet/create-three-dots-sheet.context';
import { AppLink } from '../app-link/app-link';
import { useCreatePlayer } from '../../../../../utils/src/lib/create-player.context';
import { CardCustomPreset } from './card';
import { Accepting, DragDropItem, DragResult } from '../../droppable/droppable';
import { ReactComponent as Top } from '../../../assets/icons/top-badge.svg';
import { ReactComponent as Trending } from '../../../assets/icons/trending-badge.svg';
import { ReactComponent as ExclusiveSoundPacks } from '../../../assets/icons/exclusive-soundpacks-badge.svg';
import { ReactComponent as StaffPick } from '../../../assets/icons/staffpick-badge.svg';

export type CreateCardSize = 'small' | 'medium' | 'large' | 'large-square' | 'label' | 'user-playlist';

export type CreateCardContentTypeWithoutActions = 'news' | 'contest';
const contentTypesWithoutImgOverlay: CreateCardProps['contentType'][] = ['news', 'contest'];
export const showActionsForContentType = (contentType?: CreateActionType | CreateCardContentTypeWithoutActions) => !contentTypesWithoutImgOverlay.includes(contentType as CreateActionType);

export interface CreateCardProps {
    contentType: CreateActionType | CreateCardContentTypeWithoutActions;
    title: string;
    id: string;
    label?: string;
    link?: string;
    cardSize: CreateCardSize;
    description?: Genre | ReactNode;
    tag?: ReactNode;
    onFavorite?: (id: string, onlyLike?: boolean) => void;
    isFavorite?: boolean;
    onCardPlay?: (id: string) => void;
    imageUrl: string;
    approved?: boolean
    // NOTE: Are both images needed ? Can this be abstracted or omited ?
    imageUrl2x?: string;
    isDescriptionSingleLine?: boolean;
    openInNewTab?: boolean;
    userPlaylistConfig?: {
        initials: string;
        imageBgColor: string;
    };
    hasExtraDemo?: boolean;
    preset?: CardCustomPreset;
    shareURL?: string;
    badge?: SoundPackage.BadgeEnum;
    hideMoreOptions?: boolean,
    items?: CreateCardCarouselItem[];
}

export function CardCreate({
    contentType,
    cardSize,
    approved,
    id,
    title,
    imageUrl,
    imageUrl2x,
    label,
    link,
    description,
    tag,
    openInNewTab,
    isFavorite,
    onFavorite,
    onCardPlay,
    userPlaylistConfig,
    isDescriptionSingleLine = false,
    hasExtraDemo = false,
    preset,
    shareURL,
    hideMoreOptions = false,
    badge,
    items
}: CreateCardProps) {
    const isCardPlayable = useMemo(() => !!onCardPlay, [onCardPlay]);
    const isImgOverlayAvailable = useMemo(() => showActionsForContentType(contentType), [contentType]);
    const { currentTrack, togglePlayPause } = useCreatePlayer();
    const [isCardImageLoaded, setIsCardImageLoaded] = useState(false);
    const { isAnonymous, setSelectedMedia, setShowSignUpModal } = useUserSettings();

    const playerState = usePlayerState();

    const { openThreeDotsModalSheet } = useContext(CreateThreeDotsSheetContext);

    const setCurrentMediaInContext = useCallback((showSignUpModal?: boolean) => {
        if (isAnonymous && items) {
            const currentCenterMedia = items.find((item) => item.id === id);
            if (currentCenterMedia) setSelectedMedia(currentCenterMedia, items);

            if (showSignUpModal) {
                switch (contentType) {
                    case 'pack':
                        setShowSignUpModal({ type: 'pack' });
                        break;
                    case 'label':
                        setShowSignUpModal({ type: 'label' });
                        break;
                    default:
                        setShowSignUpModal({ type: 'pack' });
                        break;
                }
            }
        }
    }, [contentType, id, isAnonymous, items, setSelectedMedia, setShowSignUpModal]);

    const handleFavorite = useCallback((onlyLike?: boolean) => {
        if (isAnonymous) {
            setCurrentMediaInContext(true);
        }
        onFavorite?.(`${id}`, onlyLike);
    }, [id, isAnonymous, onFavorite, setCurrentMediaInContext]);

    const openThreeDots = useCallback(
        async (element) => {
            if (isAnonymous) {
                // if (setCurrentMedia) setCurrentMedia(id, true);
                setCurrentMediaInContext(true);
                return;
            }
            const { top, left } = (element.target as HTMLButtonElement).getBoundingClientRect();
            openThreeDotsModalSheet({
                newCreateActionType: contentType as CreateActionType,
                actionId: id,
                left,
                top: top + window.scrollY,
                showExtraPlayDemoOption: hasExtraDemo,
                actionsToHide: isCardPlayable ? [] : ['play-demo', 'play-demo-2'],
                shareUrl: shareURL,
            });
        },
        [isAnonymous, openThreeDotsModalSheet, contentType, id, hasExtraDemo, isCardPlayable, shareURL, setCurrentMediaInContext]
    );

    const handleCardPlay = useCallback(() => {
        if (isAnonymous) {
            setCurrentMediaInContext();
        }

        if (currentTrack?.id === id) {
            togglePlayPause();
        } else {
            onCardPlay?.(id);
        }
    }, [currentTrack?.id, id, isAnonymous, onCardPlay, setCurrentMediaInContext, togglePlayPause]);

    const dragAndDropType: Accepting | undefined = useMemo(() => {
        switch (contentType) {
            case 'label':
                return 'Label';
            case 'pack':
                return 'SoundPack';
            case 'curated-set':
                return 'CuratedSet';
            default:
                return 'None';
        }
    }, [contentType]);

    const dragAndDropItem: DragDropItem = useMemo(() => {
        switch (contentType) {
            default:
                return {
                    id, title
                };
        }
    }, [contentType, id, title]);

    const renderBadge = useCallback(() => {
        if (cardSize === 'large-square') {
            switch (badge) {
                case SoundPackage.BadgeEnum.Top:
                    return <Top height={30} scale={3} width={61.25} />;
                case SoundPackage.BadgeEnum.Trending:
                    return <Trending height={30} width={93.75} />;
                case SoundPackage.BadgeEnum.Exclusive:
                    return <ExclusiveSoundPacks height={30} width={97.5} />;
                case SoundPackage.BadgeEnum.StaffPick:
                    return <StaffPick height={30} width={100} />;
                default:
                    return null;
            }
        }
        switch (badge) {
            case SoundPackage.BadgeEnum.Top:
                return <Top />;
            case SoundPackage.BadgeEnum.Trending:
                return <Trending />;
            case SoundPackage.BadgeEnum.Exclusive:
                return <ExclusiveSoundPacks />;
            case SoundPackage.BadgeEnum.StaffPick:
                return <StaffPick />;
            default:
                return null;
        }
    }, [badge, cardSize]);

    const { isMobile } = useViewport();

    // eslint-disable-next-line no-empty-pattern
    const [{ }, dragRef, connectDragPreview] = useDrag<DragDropItem, any, { opacity: number }>({
        item: dragAndDropItem,
        canDrag() {
            return !isMobile;
        },
        end: (draggedItem, monitor) => {
            if (monitor.didDrop()) {
                // eslint-disable-next-line @typescript-eslint/no-shadow
                const { target } = monitor.getDropResult() as DragResult;
                if (target === 'favorites') {
                    handleFavorite(true);
                }
            }
        },
        type: dragAndDropType
    });

    connectDragPreview(getEmptyImage(), {
        // IE fallback: specify that we'd rather screenshot the node
        // when it already knows it's being dragged so we can hide it with CSS.
        captureDraggingState: true,
    });

    return (
        <div
            ref={dragRef}
            className={classNames(
                // eslint-disable-next-line @typescript-eslint/dot-notation
                styles['card'],
                styles[`card--${cardSize}`],
                {
                    [styles['card--overlay-available']]: isImgOverlayAvailable,
                    [styles['card--qc']]: approved === false,
                    [styles['card--plain-link']]: !isImgOverlayAvailable,
                    [styles[`card--custom-preset-${preset}`]]: !!preset
                }
            )}
        >
            <div
                className={classNames(styles['card__img-container'], {
                    [styles['card__img-container--small-img']]: userPlaylistConfig,
                    [styles['card__img-container--no-img']]: !imageUrl,
                })}
                style={userPlaylistConfig && { backgroundColor: `${userPlaylistConfig.imageBgColor}E6` }}
            >
                {tag}
                {imageUrl ? <img src={imageUrl} draggable={false} alt={title} srcSet={imageUrl2x ? `${imageUrl2x} 2x` : undefined} onLoad={() => setIsCardImageLoaded(true)} /> : <MissingImgPlaceholder isSmallImg={!!userPlaylistConfig} />}
                {badge && isCardImageLoaded ? (
                    <div className={classNames(styles['card__badge-container'], styles[`card__badge-container--${cardSize}`])}>
                        <div className={styles['card__badge']}>
                            {renderBadge()}
                        </div>
                    </div>
                ) : null}
                {label && <span className={styles['card__label']}>{label}</span>}
                {userPlaylistConfig && (
                    <span
                        className={classNames(styles['card__user-playlist-initials'], {
                            [styles['card__user-playlist-initials--black-text']]: isHexColorLight(userPlaylistConfig.imageBgColor),
                        })}
                    >
                        {userPlaylistConfig.initials}
                    </span>
                )}
                {isImgOverlayAvailable && (
                    <div className={classNames(styles['card__img-overlay'])}>
                        <div className={styles['card__card-actions-overlay']}>
                            {!hideMoreOptions ?
                                (
                                    <button aria-label="More Options" type="button" onClick={openThreeDots} className={classNames(styles['card__more-dots-container'], styles['card__more-dots-toggler'])}>
                                        <ThreeDotsIcon />
                                    </button>
                                ) : null}
                            {onFavorite && (
                                <button
                                    className={classNames(styles['card__favorite-heart'], { [styles['card__favorite-heart--unfavorite']]: isFavorite })}
                                    type="button"
                                    aria-label="Favorite"
                                    onClick={() => handleFavorite()}
                                >
                                    {isFavorite ? <UnfavoriteHeart /> : <FavoriteHeart />}
                                </button>
                            )}
                            {isCardPlayable && (
                                <button className={styles['card__play-btn']} type="button" aria-label="Play" onClick={handleCardPlay}>
                                    {/* eslint-disable-next-line max-len */}
                                    {currentTrack?.id === id && playerState === State.Playing ? <PauseIcon /> : <PlayArrow />}
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>
            <div className={styles['card__text-container']}>
                {(link || !isCardPlayable) &&
                    (link ? (
                        <AppLink href={link}>
                            <a target={openInNewTab ? '_blank' : undefined} className={classNames(styles['card__title'], styles['card__title--create'])}>
                                {title}
                            </a>
                        </AppLink>
                    ) : (
                        <span className={classNames(styles['card__title'], styles['card__title--create'])}>{title}</span>
                    ))}
                {!link && isCardPlayable && (
                    <button type="button" className={classNames(styles['card__title'], styles['card__title--play-only'])} onClick={handleCardPlay}>
                        {title}
                    </button>
                )}
                {description && (
                    <div className={styles['card__create-description']}>{isDescriptionSingleLine ? <div className={styles['card__single-line-description']}>{description}</div> : description}</div>
                )}
            </div>
        </div>
    );
}

export default memo(CardCreate);
